/// <amd-module name="VSS/Features/PlatformUI/UnsavedChangesService" />
import * as Resources from "VSS/Features/PlatformUI/Resources";
import { FastPageSwitchEventDetail, IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { showMessageDialog } from "./MessageDialog";

/**
 * Service for protecting a user against accidentally losing unsaved changes.
 */
export interface IUnsavedChangesService extends IVssService {
    /**
     * Register an item that may have unsaved changes. Prompt the user
     * to confirm losing changes before navigating away
     *
     * @param document Unsaved document details
     */
    register(document: IUnsavedDocument): void;
    
    /**
     * Register an item that may have unsaved changes but make it first in the list. Prompt the user
     * to confirm losing changes before navigating away
     *
     * @param document Unsaved document details
     */
     registerFirst(document: IUnsavedDocument): void;

    /**
     * Unregister an unsaved document
     *
     * @param document Unsaved document details
     */
    unregister(document: IUnsavedDocument): void;

    /**
     * Trigget the navigate-away check for dirty documents
     *
     * @param onLeave Callback invoked if the user chooses to leave
     * @param onStay Optional callback invoked if the user chooses to stay
     */
    triggerNavigateAway(onLeave: () => void, onStay?: () => void): void;
}

/**
 * Details about an item that may have unsaved changes
 */
export interface IUnsavedDocument {
    /**
     * Optional callback to determine if this item has dirty changes.
     * If undefined, this item is assumed to always be in the dirty state.
     */
    isDirty?: () => boolean;

    /**
     * Gets a custom title to display in the dialog/prompt to the user
     */
    getDirtyTitle?: () => string | undefined;

    /**
     * Gets a custom button leave name to display in the dialog/prompt to the user
     */
    getDirtyOnLeaveBtn?: () => string | undefined;

    /**
     * Gets a custom button stay name to display in the dialog/prompt to the user
     */
    getDirtyOnStayBtn?: () => string | undefined;

    /**
     * Gets a custom message to display in the dialog/prompt to the user
     */
    getDirtyMessage?: () => string | undefined;
}

class UnsavedChangesService extends VssService implements IUnsavedChangesService {
    private documents: IUnsavedDocument[] = [];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        document.body.addEventListener("fpsStarting", this.onFpsStarting);
        window.addEventListener("beforeunload", this.onBeforeUnload);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        document.body.removeEventListener("fpsStarting", this.onFpsStarting);
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    }

    public register(document: IUnsavedDocument): void {
        this.documents.push(document);
    }

    public registerFirst(document: IUnsavedDocument): void {
        this.documents.unshift(document);
    }

    public unregister(document: IUnsavedDocument): void {
        this.documents = this.documents.filter(d => d !== document);
    }

    public triggerNavigateAway(onLeave: () => void, onStay?: () => void): void {
        const message = this.getDirtyDocumentMessage();
        const title = this.getDirtyDocumentTitle();
        const leaveBtn = this.getDirtyDocumentOnLeaveBtn();
        const stayBtn = this.getDirtyDocumentOnStayBtn();
        if (message && title && leaveBtn && stayBtn) {
            this.showMessage(title, message, leaveBtn, stayBtn, onLeave, onStay);
        } else {
            onLeave();
        }
    }

    private onBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
        const message = this.getDirtyDocumentMessage();
        if (message) {
            e.returnValue = message;
            e.preventDefault();
            return message;
        }

        return undefined;
    };

    private onFpsStarting = (e: CustomEvent<FastPageSwitchEventDetail>): void => {
        const message = this.getDirtyDocumentMessage();
        const title = this.getDirtyDocumentTitle();
        const leaveBtn = this.getDirtyDocumentOnLeaveBtn();
        const stayBtn = this.getDirtyDocumentOnStayBtn();
        if (message && title && leaveBtn && stayBtn && !(e.detail.data && e.detail.data.userConfirmed)) {
            e.preventDefault();
            e.detail.cancellationReason = "dirtyState";
            this.showMessage(title, message, leaveBtn, stayBtn, () => {
                FastPageSwitch(e.detail.pageContext, e.detail.href, e.detail.recordHistoryEntry, { ...e.detail.data, userConfirmed: true });
            });
        }
    };

    private getDirtyDocumentMessage = (): string | undefined => {
        for (const document of this.documents) {
            if (!document.isDirty || document.isDirty()) {
                return (document.getDirtyMessage && document.getDirtyMessage()) || Resources.unsavedChangesDefaultMessage;
            }
        }
        return undefined;
    };

    private getDirtyDocumentTitle = (): string | undefined => {
        for (const document of this.documents) {
            if (!document.isDirty || document.isDirty()) {
                return (document.getDirtyTitle && document.getDirtyTitle()) || Resources.leavePageDialogTitle
            }
        }
        return undefined;
    };

    private getDirtyDocumentOnStayBtn = (): string | undefined => {
        for (const document of this.documents) {
            if (!document.isDirty || document.isDirty()) {
                return (document.getDirtyOnStayBtn && document.getDirtyOnStayBtn()) || Resources.stay
            }
        }
        return undefined;
    };

    private getDirtyDocumentOnLeaveBtn = (): string | undefined => {
        for (const document of this.documents) {
            if (!document.isDirty || document.isDirty()) {
                return (document.getDirtyOnLeaveBtn && document.getDirtyOnLeaveBtn()) || Resources.leave
            }
        }
        return undefined;
    };

    private showMessage(title: string, message: string, leaveBtn: string, stayBtn: string, onLeave: () => void, onStay?: () => void): void {
        showMessageDialog(this.pageContext, {
            cancelButtonProps: {
                text: stayBtn
            },
            lightDismiss: false,
            message,
            onClose: result => {
                if (result) {
                    onLeave();
                } else if (onStay) {
                    onStay();
                }
            },
            okButtonProps: {
                text: leaveBtn
            },
            title: title
        });
    }
}

Services.add("IUnsavedChangesService", { serviceFactory: UnsavedChangesService });
